import React, { useMemo, useCallback, useEffect } from 'react';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useBookingStore } from '../../store/bookingStore';

const DateSelector = ({ 
  availableSlots, 
  onDateSelect, 
  selectedDate: propSelectedDate,
  primaryColor,
  loading
}) => {
  // Usar o store para obter estado e funções
  const {
    date: storeDate,
    setDate: storeSetDate
  } = useBookingStore();
  
  // Usar props ou valores do store
  const selectedDate = propSelectedDate !== undefined ? propSelectedDate : storeDate;
  
  // Handler que usa a função do store ou a prop onDateSelect
  const handleDateSelect = useCallback((date) => {
    if (date === selectedDate) return; // Evitar seleções redundantes
    
    if (onDateSelect) {
      onDateSelect(date);
    } else {
      storeSetDate(date);
    }
  }, [selectedDate, onDateSelect, storeSetDate]);
  
  // Usar fallback para datas disponíveis se necessário
  const fallbackDates = useMemo(() => {
    // Se já temos datas ou estamos carregando, não precisamos do fallback
    if ((availableSlots && availableSlots.length > 0) || loading) {
      return [];
    }
    
    // Criar um array de datas disponíveis para os próximos 14 dias
    const today = new Date();
    const dates = [];
    
    for (let i = 0; i < 14; i++) {
      const date = addDays(today, i);
      dates.push({
        date: format(date, 'yyyy-MM-dd'),
        available: true
      });
    }
    
    return dates;
  }, [availableSlots, loading]);
  
  // Memoizar a transformação dos dados de datas para evitar recálculos desnecessários
  const calendarDays = useMemo(() => {
    // Usar as datas disponíveis ou o fallback se não houver datas
    const datesToUse = (availableSlots && availableSlots.length > 0) 
      ? availableSlots 
      : fallbackDates;
    
    return datesToUse.map(slot => {
      const date = new Date(slot.date);
      return {
        date,
        formattedDate: slot.date,
        dayOfMonth: format(date, 'd'),
        dayOfWeek: format(date, 'E', { locale: ptBR }),
        isAvailable: slot.available
      };
    });
  }, [availableSlots, fallbackDates]);
  
  // Se não temos data selecionada, selecionar a primeira disponível
  useEffect(() => {
    if (!selectedDate && calendarDays.length > 0 && !loading) {
      const firstAvailableDay = calendarDays.find(day => day.isAvailable);
      if (firstAvailableDay) {
        handleDateSelect(firstAvailableDay.formattedDate);
      }
    }
  }, [calendarDays, selectedDate, handleDateSelect, loading]);

  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium mb-3">Selecione uma data:</h3>
      <div className="flex overflow-x-auto pb-2 -mx-4 px-4">
        <div className="flex space-x-3">
          {loading ? (
            <div className="flex items-center justify-center w-full py-4">
              <p className="text-gray-500">Carregando datas disponíveis...</p>
            </div>
          ) : calendarDays.length > 0 ? (
            calendarDays.map(day => (
              <button
                key={day.formattedDate}
                onClick={() => handleDateSelect(day.formattedDate)}
                disabled={!day.isAvailable}
                className={`
                  flex flex-col items-center justify-center min-w-[65px] h-20
                  ${selectedDate === day.formattedDate 
                    ? 'text-white' 
                    : day.isAvailable 
                      ? 'bg-white text-gray-800 border border-gray-200'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
                  rounded-lg shadow-sm transition-colors
                `}
                style={selectedDate === day.formattedDate ? { backgroundColor: primaryColor } : {}}
              >
                <span className="text-xs uppercase font-medium">{day.dayOfWeek}</span>
                <span className="text-xl font-bold mt-1">{day.dayOfMonth}</span>
              </button>
            ))
          ) : (
            <div className="flex items-center justify-center w-full py-4">
              <p className="text-gray-500">Nenhuma data disponível.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(DateSelector);
